import { useEffect, useMemo, useRef, useState } from "react";
import { ROUND_VARIANTS } from "../constants/upDownConstants";
import { resolveVariantIdByDuration, roundToCents } from "../utils/upDownFormatters";
import { buildTradePanelParticipants } from "../utils/houseBetsGenerator";

/**
 * Custom hook to simulate and manage round participants,
 * side bets, pool totals, and player lists.
 */
export function useUpDownParticipants({
  latestOrder,
  variant,
  variantId,
  amount,
  effectivePayoutMultiplier,
}) {
  const [areTradeParticipantsVisible, setAreTradeParticipantsVisible] = useState(false);
  const [isTradePanelClosing, setIsTradePanelClosing] = useState(false);
  const [tradeParticipants, setTradeParticipants] = useState([]);

  const tradeParticipantsRevealTimerRef = useRef(null);
  const tradePanelCloseTimerRef = useRef(null);
  const tradePanelFinalizeTimerRef = useRef(null);

  useEffect(() => {
    if (!latestOrder) {
      setAreTradeParticipantsVisible((previous) => (previous ? false : previous));
      setTradeParticipants((previous) => (previous.length > 0 ? [] : previous));
      return;
    }

    if (latestOrder.status !== "active") {
      return;
    }

    const orderId = String(latestOrder.id || "").trim();
    const hasParticipantsForOrder = tradeParticipants.some((participant) =>
      String(participant?.id || "").includes(orderId),
    );
    if (hasParticipantsForOrder) {
      setAreTradeParticipantsVisible(true);
      setIsTradePanelClosing(false);
      return;
    }

    const restoredVariant =
      ROUND_VARIANTS.find(
        (option) =>
          option.id === resolveVariantIdByDuration(latestOrder.durationSeconds),
      ) ??
      ROUND_VARIANTS.find((option) => option.id === variantId) ??
      variant;

    setTradeParticipants(
      buildTradePanelParticipants({
        side: latestOrder.side,
        amount: latestOrder.amount,
        minimumStake: restoredVariant.minimumStake,
        maximumStake: restoredVariant.maximumStake,
        orderId,
      }),
    );
    setAreTradeParticipantsVisible(true);
    setIsTradePanelClosing(false);
  }, [latestOrder, tradeParticipants, variant, variantId]);

  useEffect(
    () => () => {
      if (tradeParticipantsRevealTimerRef.current !== null) {
        clearTimeout(tradeParticipantsRevealTimerRef.current);
        tradeParticipantsRevealTimerRef.current = null;
      }
      if (tradePanelCloseTimerRef.current !== null) {
        clearTimeout(tradePanelCloseTimerRef.current);
        tradePanelCloseTimerRef.current = null;
      }
      if (tradePanelFinalizeTimerRef.current !== null) {
        clearTimeout(tradePanelFinalizeTimerRef.current);
        tradePanelFinalizeTimerRef.current = null;
      }
    },
    [],
  );

  const shouldShowTradeParticipants =
    Boolean(
      latestOrder &&
      latestOrder.status !== "settled" &&
      tradeParticipants.length > 0,
    ) || areTradeParticipantsVisible;

  const displayBets = useMemo(
    () => (shouldShowTradeParticipants ? tradeParticipants : []),
    [shouldShowTradeParticipants, tradeParticipants],
  );

  const investmentAmount =
    latestOrder?.status !== "settled" &&
    Number.isFinite(Number(latestOrder?.amount))
      ? Number(latestOrder.amount)
      : amount;

  const pools = useMemo(() => {
    const totals = {
      upAmount: 0,
      downAmount: 0,
      upCount: 0,
      downCount: 0,
    };

    for (const bet of displayBets) {
      if (bet.side === "up") {
        totals.upAmount += bet.amount;
        totals.upCount += 1;
      } else {
        totals.downAmount += bet.amount;
        totals.downCount += 1;
      }
    }

    return {
      ...totals,
      totalAmount: totals.upAmount + totals.downAmount,
    };
  }, [displayBets]);

  const upPotentialReturn = useMemo(
    () => roundToCents(investmentAmount * effectivePayoutMultiplier),
    [investmentAmount, effectivePayoutMultiplier],
  );
  const downPotentialReturn = useMemo(
    () => roundToCents(investmentAmount * effectivePayoutMultiplier),
    [investmentAmount, effectivePayoutMultiplier],
  );

  const upPlayers = useMemo(
    () =>
      displayBets
        .filter((bet) => bet.side === "up")
        .sort((left, right) => right.amount - left.amount),
    [displayBets],
  );

  const downPlayers = useMemo(
    () =>
      displayBets
        .filter((bet) => bet.side === "down")
        .sort((left, right) => right.amount - left.amount),
    [displayBets],
  );

  const sideUpPlayers = useMemo(() => upPlayers, [upPlayers]);
  const sideDownPlayers = useMemo(() => downPlayers, [downPlayers]);

  const sidePools = useMemo(
    () => ({
      upCount: sideUpPlayers.length,
      downCount: sideDownPlayers.length,
      upAmount: sideUpPlayers.reduce((total, bet) => total + bet.amount, 0),
      downAmount: sideDownPlayers.reduce((total, bet) => total + bet.amount, 0),
    }),
    [sideDownPlayers, sideUpPlayers],
  );

  const mobileUpPlayers = useMemo(
    () => sideUpPlayers.slice(0, 4),
    [sideUpPlayers],
  );
  const mobileDownPlayers = useMemo(
    () => sideDownPlayers.slice(0, 4),
    [sideDownPlayers],
  );

  const compactUpPlayers = useMemo(
    () => sideUpPlayers.slice(0, 3),
    [sideUpPlayers],
  );
  const compactDownPlayers = useMemo(
    () => sideDownPlayers.slice(0, 3),
    [sideDownPlayers],
  );

  const resetParticipants = () => {
    setAreTradeParticipantsVisible(false);
    setIsTradePanelClosing(false);
    setTradeParticipants([]);
  };

  return {
    areTradeParticipantsVisible,
    setAreTradeParticipantsVisible,
    isTradePanelClosing,
    setIsTradePanelClosing,
    tradeParticipants,
    setTradeParticipants,
    shouldShowTradeParticipants,
    displayBets,
    pools,
    upPotentialReturn,
    downPotentialReturn,
    sideUpPlayers,
    sideDownPlayers,
    sidePools,
    mobileUpPlayers,
    mobileDownPlayers,
    compactUpPlayers,
    compactDownPlayers,
    resetParticipants,
  };
}

